import { z } from 'zod';

import { getPlanSchema } from './PlanSchema';

// 검증 상수 - export해서 다른 곳에서도 사용 가능
export const VALIDATION_RULES = {
  ORGANIZATION_NAME: { MIN: 1, MAX: 30 },
  REFERENCE_EMAIL: { MIN: 6, MAX: 100 },
  NUMBER_OF_USERS: { MIN: 1, MAX: 2147483647 },
} as const;

// PaymentFormData가 정의된 후 사용할 예정이므로 먼저 추출
type ValidationFieldMessages = {
  min?: string;
  max?: string;
  required?: string;
  format?: string;
};

// 검증이 필요한 필드들을 명시적으로 정의
type ValidationKeys =
  | 'organizationName'
  | 'referenceEmail'
  | 'startDate'
  | 'maxUserCount'
  | 'termsAgreed';

// 타입 안전한 ValidationMessages 정의
export type TypedValidationMessages = {
  [K in ValidationKeys]: ValidationFieldMessages;
};

// 함수형 팩토리: 에러 메시지 Record를 받아서 스키마 생성
export const createCheckoutSchema = (messages: TypedValidationMessages) =>
  z
    .object({
      selectedPlan: getPlanSchema(),
      schoolPackageMode: z.boolean().default(false),

      organizationName: z
        .string()
        .min(
          VALIDATION_RULES.ORGANIZATION_NAME.MIN,
          messages.organizationName.min,
        )
        .max(
          VALIDATION_RULES.ORGANIZATION_NAME.MAX,
          messages.organizationName.max,
        )
        .optional(),
      skipOrganizationName: z.boolean().default(false),
      referenceEmail: z
        .string()
        .optional()
        .refine(
          value =>
            !value ||
            value.length === 0 ||
            (value.length >= VALIDATION_RULES.REFERENCE_EMAIL.MIN &&
              value.length <= VALIDATION_RULES.REFERENCE_EMAIL.MAX &&
              z.string().email().safeParse(value).success),
          messages.referenceEmail?.format,
        ),

      // 선택 개월수
      selectedMonth: z.string(),

      // 시작일
      startDate: z.string().min(1, messages.startDate.required),

      // 구매 정원은 양의 정수이며 DB의 Int 범위까지 입력할 수 있다.
      maxUserCount: z.string().min(1, messages.maxUserCount.required),

      // 약관 동의
      termsAgreed: z.boolean().refine(val => val === true, {
        message: messages.termsAgreed.required,
      }),
    })
    .refine(
      data =>
        /^\d+$/.test(data.maxUserCount) &&
        Number(data.maxUserCount) >= 1 &&
        Number(data.maxUserCount) <= VALIDATION_RULES.NUMBER_OF_USERS.MAX,
      { message: messages.maxUserCount.required, path: ['maxUserCount'] },
    )
    .refine(
      data => {
        // skipOrganizationName이 false면 organizationName 필수
        return data.skipOrganizationName || !!data.organizationName;
      },
      {
        message: messages.organizationName.required,
        path: ['organizationName'],
      },
    );

export type PaymentFormData = z.infer<ReturnType<typeof createCheckoutSchema>>;
